/**
 * 데스크탑 앱(`desktop/`)이 webview로 주입하는 `window.agentteamsBridge` 연동 유틸.
 *
 * - 본 모듈은 브라우저 일반 사용자에게는 영향이 없다 (`window.agentteamsBridge`가 없으면 no-op).
 * - 데스크탑 환경에서만 활성화되어 캡처 결과를 localStorage에 임시 보관하고
 *   DaemonTriggerModal 등 후속 UI에서 해당 첨부를 발견·소비할 수 있게 한다.
 */

export const DESKTOP_PENDING_CAPTURE_KEY = 'agentteams.desktop.pendingCapture';
export const DESKTOP_PENDING_CAPTURE_EVENT = 'agentteams:desktopPendingCaptureChanged';
export const DESKTOP_DEVICE_ID_KEY = 'agentteams.desktop.deviceId';

export type DesktopBridgeCapturePayload = {
  captureId: string;
  pngDataUrl: string;
  width: number;
  height: number;
  scaleFactor: number;
  capturedAt: string;
  platform: string;
  description: string;
  rawOnly: boolean;
  /**
   * 데스크탑 어노테이션에서 "이어쓰기로 보내기"를 선택했을 때 true.
   * 기본은 false(신규 요청 모드). desktop 측은 본 플랜의 T4에서 채운다.
   */
  followUp?: boolean;
};

export type DesktopSocialAuthProvider = 'google' | 'github' | 'apple' | 'microsoft';

export type AgentTeamsBridge = {
  readonly ready: true;
  readonly version: string;
  onCapture(callback: (payload: DesktopBridgeCapturePayload) => void): () => void;
  onLifecycleState?(callback: (payload: DesktopLifecycleStatePayload) => void): () => void;
  onRealtimeOwnerState?(callback: (payload: DesktopRealtimeOwnerStatePayload) => void): () => void;
  onRealtimeEvent?(callback: (payload: DesktopRealtimeEventPayload) => void): () => void;
  /** 셸 사이드 메뉴 설정 버튼 → 홈 화면에서 워크스페이스 설정 모달을 열도록 하는 신호. */
  onSettingsOpen?(callback: () => void): () => void;
  request(type: string, payload?: Record<string, unknown>): void;
};

export type DesktopLifecycleStatePayload = {
  state: 'background' | 'foreground';
  isVisible: boolean;
};

export type DesktopRealtimeOwnerStatePayload = {
  instanceId?: string;
  granted: boolean;
  ownerWebContentsId?: number | null;
  reason?: string;
};

export type DesktopRealtimeEventPayload = {
  instanceId?: string;
  eventName: string;
  data: string;
  lastEventId: string;
};

declare global {
  interface Window {
    agentteamsBridge?: AgentTeamsBridge;
  }
}

export function hasDesktopBridge(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(window.agentteamsBridge?.ready);
}

export function getOrCreateDesktopDeviceId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.localStorage.getItem(DESKTOP_DEVICE_ID_KEY);
    if (existing) return existing;
    const next =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(DESKTOP_DEVICE_ID_KEY, next);
    return next;
  } catch {
    return `${Date.now()}-fallback`;
  }
}

export function getDesktopPushEndpoint(): string {
  return `desktop://${getOrCreateDesktopDeviceId()}`;
}

export function startDesktopSocialAuth(
  provider: DesktopSocialAuthProvider,
  authUrl: string,
  redirectPath?: string | null,
): boolean {
  if (!hasDesktopBridge()) return false;
  window.agentteamsBridge?.request('auth:social-start', {
    provider,
    authUrl,
    redirectPath: redirectPath ?? null,
  });
  return true;
}

export function notifyDesktopLogout(reason?: string | null): boolean {
  if (!hasDesktopBridge()) return false;
  window.agentteamsBridge?.request('auth:logout', { reason: reason ?? null });
  return true;
}

/**
 * 데스크탑 사이드 패널이 프로젝트 탭을 렌더링하는 데 필요한 최소 정보.
 * desktop 측 `ProjectDescriptor`와 형태를 맞춘다.
 */
export type DesktopProjectDescriptor = {
  projectId: string;
  teamId: string;
  name: string;
  teamName: string;
};

/**
 * 사용자의 프로젝트 목록을 데스크탑 셸로 보고한다(사이드 패널 소스).
 * 데스크탑 환경이 아니면 no-op이라 일반 브라우저에는 영향이 없다.
 *
 * @param attentionByProjectId 프로젝트별 "처리할 항목(주의)" 카운트. 데스크탑 사이드 메뉴 뱃지로 렌더된다.
 */
export function reportProjectsToDesktop(
  projects: DesktopProjectDescriptor[],
  attentionByProjectId?: Record<string, number>,
): boolean {
  if (!hasDesktopBridge()) return false;
  window.agentteamsBridge?.request('projects:report', { projects, attentionByProjectId: attentionByProjectId ?? {} });
  return true;
}

/**
 * 사용자가 홈에서 선택한 프로젝트를 데스크탑 셸 탭으로 열고 사이드 메뉴에 고정하도록 요청한다.
 * 데스크탑 환경이 아니면 false를 반환하므로 호출부가 일반 브라우저용 라우팅으로 분기할 수 있다.
 */
export function openProjectInDesktop(project: DesktopProjectDescriptor, path?: string): boolean {
  if (!hasDesktopBridge()) return false;
  window.agentteamsBridge?.request('project:open', path ? { ...project, path } : { ...project });
  return true;
}

export function getPendingCapture(): DesktopBridgeCapturePayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DESKTOP_PENDING_CAPTURE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DesktopBridgeCapturePayload;
    if (typeof parsed?.pngDataUrl !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setPendingCapture(payload: DesktopBridgeCapturePayload | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (payload === null) {
      window.localStorage.removeItem(DESKTOP_PENDING_CAPTURE_KEY);
    } else {
      window.localStorage.setItem(DESKTOP_PENDING_CAPTURE_KEY, JSON.stringify(payload));
    }
    window.dispatchEvent(new CustomEvent(DESKTOP_PENDING_CAPTURE_EVENT));
  } catch {
    // localStorage가 비활성/할당량 초과인 경우 — 데스크탑 캡처는 보관 못 해도 토스트는 띄운다.
  }
}

export function subscribePendingCapture(callback: (payload: DesktopBridgeCapturePayload | null) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (): void => callback(getPendingCapture());
  window.addEventListener(DESKTOP_PENDING_CAPTURE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(DESKTOP_PENDING_CAPTURE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

export function formatCaptureSize(payload: DesktopBridgeCapturePayload): string {
  if (payload.rawOnly) return '원본 캡처';
  return `${payload.width || '?'} × ${payload.height || '?'}`;
}
